import React, { useContext, useMemo } from 'react';
import { Box, Button, Modal } from '@zillow/constellation-10';
import { TourActionButton } from '@zg-rentals/ts-z-calendar-client';

import { RescheduleTourModalTriggers } from '../../../../constants';
import type { TourEvent } from '../../../../constants/TourCalendar';
import { getTourDateAndTime } from '../../../../utils/formatTime';
import CancelTourConfirmationModal from '../TourEventPopoverBody/components/CancelTourConfirmationModal/CancelTourConfirmationModal';
import RescheduleTourModal from '../../../RescheduleTourModal';
import EventDetails from '../../../EventDetails';

import TourCalendarContext from '../../../TourCalendarWithSettings/context/TourCalendarContext';

type TourEventPreviewPopoverBodyProps = {
  children?: JSX.Element;
  event: TourEvent;
  header: React.ReactNode;
  isOpen?: boolean;
  onCancelTourConfirmationClick: () => void;
  onClose: () => void;
  onConfirmClick: () => void;
  onDeclineClick: () => void;
  onRescheduleSuccess: () => void;
};

const TourEventModal = ({
  children,
  event,
  header,
  isOpen,
  onCancelTourConfirmationClick,
  onClose,
  onConfirmClick,
  onDeclineClick,
  onRescheduleSuccess,
}: TourEventPreviewPopoverBodyProps) => {
  const { clickStreamMetadata, listingAddress, listingAlias, propertyPhotoUrl } = useContext(TourCalendarContext);
  const [showCancelTourConfirmationModal, setShowCancelTourConfirmationModal] = React.useState(false);
  const [showRescheduleTourModal, setShowRescheduleTourModal] = React.useState(false);
  const tourDateAndTime = useMemo(() => getTourDateAndTime(event.start, event.end), [event]);
  const shouldDisplayFooter = event.allowedLandlordActions.length > 0 && !event.isPastEvent;

  const handleRescheduleTourInitialClick = () => {
    setShowRescheduleTourModal(true);
  };

  const handleCancelTourInitialClick = () => {
    setShowCancelTourConfirmationModal(true);
  };

  const tourEventFooterButtons = useMemo(() => {
    const eventAllowsCancel = event.allowedLandlordActions.includes(TourActionButton.CANCEL);
    const eventAllowsConfirm = event.allowedLandlordActions.includes(TourActionButton.CONFIRM);
    const eventAllowsDecline = event.allowedLandlordActions.includes(TourActionButton.DECLINE);
    const eventAllowsReschedule = event.allowedLandlordActions.includes(TourActionButton.IRENE_RESCHEDULE);

    const actionButtons = [];

    if (eventAllowsConfirm) {
      actionButtons.push(
        <Button
          css={{ gridColumn: '1 / -1' }}
          data-testid="TourEventModal-ConfirmBtn"
          emphasis="filled"
          key="confirm"
          onClick={onConfirmClick}
        >
          Confirm tour
        </Button>,
      );
    }

    if (eventAllowsReschedule) {
      actionButtons.push(
        <Button
          data-testid="TourEventModal-RescheduleBtn"
          key="reschedule"
          onClick={handleRescheduleTourInitialClick}
          tone="neutral"
        >
          Reschedule
        </Button>,
      );
    }

    if (eventAllowsDecline) {
      actionButtons.push(
        <Button data-testid="TourEventModal-DeclineBtn" key="decline" onClick={onDeclineClick} tone="neutral">
          Decline
        </Button>,
      );
    }

    if (eventAllowsCancel) {
      actionButtons.push(
        <Button
          data-testid="TourEventModal-CancelBtn"
          key="cancel"
          onClick={handleCancelTourInitialClick}
          tone="neutral"
        >
          Cancel
        </Button>,
      );
    }

    return actionButtons;
  }, [event]);

  return (
    <>
      <Modal
        body={
          <EventDetails
            bookedByName={event.bookedByName}
            eventDateAndTime={tourDateAndTime}
            listingStreet={listingAddress || ''}
            photoUrl={propertyPhotoUrl}
            title={event.title}
            tourStatus={event.tourStatus}
          />
        }
        dividers
        footer={
          shouldDisplayFooter && (
            <Box
              css={{
                display: 'grid',
                gridTemplateRows: 'auto',
                gridTemplateColumns: '1fr 1fr',
                gap: 'default',
                width: '100%',
              }}
              data-testid="TourEventModal-Footer"
            >
              {tourEventFooterButtons}
            </Box>
          )
        }
        header={header}
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
        trigger={children}
      />
      {showCancelTourConfirmationModal && (
        <CancelTourConfirmationModal
          event={event}
          onClose={() => setShowCancelTourConfirmationModal(false)}
          onCancelTourConfirmationClick={onCancelTourConfirmationClick}
        />
      )}
      {showRescheduleTourModal && listingAlias && (
        <RescheduleTourModal
          clickStreamUser={clickStreamMetadata?.user}
          listingAlias={listingAlias}
          tourId={event.id}
          tourTitle={event.title}
          triggerSource={RescheduleTourModalTriggers.ZRM_TOUR_CALENDAR}
          onClose={() => setShowRescheduleTourModal(false)}
          onRescheduleSuccess={onRescheduleSuccess}
        />
      )}
    </>
  );
};

export default TourEventModal;
